// Mock data v2 — reshaped to speak the backend schema's language.
//
// Sources of truth (see /schemas from the BE dev):
//   class.schema.json + examples/class.sundaySchool.json  -> class documents
//   examples/family.v2.json                               -> family blobs
//
// Class documents follow the schema example exactly:
//   { type, class, classroom, day, teachers, teacherV2, students }
// - NO classId / name / roomNumber / meetingDay. The class identity IS the
//   "class" value ("21", "1В", "4А"); documents are unique by
//   type + class + classroom + day.
// - "students" are EMBEDDED and denormalized (childId, familyId, studentName,
//   birthdate, gender, parentName1/2, parentPhone1/2) — no refs to resolve.
// - "teacherV2" [{teacherName, phone, role}] is the source of truth for
//   teacher accounts (phone = login); the legacy "teachers" names array is
//   kept in parallel, exactly like the example.
// - Phones are 10-digit national strings ("5550001111"), per the schema
//   pattern. Demo logins keep working: the login normalizer strips +1.
// - "location" ("Church 1" / "Church 2", family.v2 wording) is OPTIONAL and
//   absent on most records — the first real run has no locations at all.
//   A few keep it so the show-when-present UI still demos.
//
// OPEN QUESTION for the BE dev — choir modeling:
// class.schema.json says "Choir is not a class document; it stays nested on
// the family child" and only enumerates type: sundaySchool | russianSchool.
// But the concept UI needs choirs as nav destinations with rosters, so this
// mock EXTENDS the class document with type "childrensChoir", where:
//   "class"     holds the choirNumber   (family.v2: children[].childrensChoir.choirNumber)
//   "classroom" holds the choirRoom     (family.v2: children[].childrensChoir.choirRoom)
//   "teacherV2" holds choirLeaderV2-style entries with role "choir_leader"
//               (keyed teacherName rather than choirLeaderName, to keep one
//               teacher-account shape across all class documents)
// If the BE would rather ship choirs another way (separate doc type, or only
// nested on families), the FE consumes them through buildTeacherModule /
// buildAdminModule and can adapt.
//
// One canonical dataset, three projections ("modules"):
//   family module  -> buildFamilyModule(phone)   what parents see today
//   teacher module -> buildTeacherModule(phone)  rosters for YOUR classes
//   admin module   -> buildAdminModule()         all classes + searchable students
// A login's modules come from resolveModules(phone).
//
// Location coherence rule: a class with a location only contains students
// whose family has the same location; classes without location only contain
// families without location.

// ---------------------------------------------------------------------------
// Canonical data — families (family.v2 blob shape)
// ---------------------------------------------------------------------------

// Hand-written families. Names are Russified, "Фамилия Имя" order like the
// family.v2 example; phone DIGITS are unchanged from the old mock so any
// remembered demo parent login still resolves. Children's embedded activity
// blocks (sundaySchool / russianSchool / childrensChoir incl. teacherV2 /
// choirLeaderV2) are attached programmatically below from the class
// documents, so blob and class docs can never drift apart.
export const families = [
  {
    familyId: 'F001',
    location: 'Church 1',
    parentName1: 'Дорошенко Иван',
    parentPhone1: '2345678901',
    parentName2: 'Дорошенко Женя',
    parentPhone2: '0987654321',
    lastUpdated: '2025-09-13',
    children: [
      { childId: 'C001', studentName: 'Дорошенко Алиса', birthdate: '2015-04-12', gender: 'female' },
      { childId: 'C002', studentName: 'Дорошенко Боря', birthdate: '2017-08-25', gender: 'male' },
    ],
  },
  {
    familyId: 'F002',
    location: 'Church 1',
    parentName1: 'Иванова Мария',
    parentPhone1: '5551234567',
    parentName2: 'Иванов Сергей',
    parentPhone2: '5557654321',
    lastUpdated: '2025-08-30',
    children: [
      { childId: 'C003', studentName: 'Иванова Даша', birthdate: '2015-09-03', gender: 'female' },
    ],
  },
  {
    familyId: 'F003',
    location: 'Church 1',
    parentName1: 'Волков Павел',
    parentPhone1: '5559990000',
    lastUpdated: '2025-09-02',
    children: [
      { childId: 'C004', studentName: 'Волкова Лена', birthdate: '2018-06-30', gender: 'female' },
    ],
  },
]

// ---------------------------------------------------------------------------
// Canonical data — class documents (schema shape, assembled below)
// ---------------------------------------------------------------------------

// Named teachers keep their old demo phones (digits unchanged) so existing
// demo logins keep working — e.g. 5550001111 is Смирнова Анна (was Ms. Smith),
// who teaches Sunday School 21, Russian School 1В and Choir 1.
const T = {
  smith: { teacherName: 'Смирнова Анна', phone: '5550001111' },
  brown: { teacherName: 'Бондаренко Пётр', phone: '5550002222' },
  thompson: { teacherName: 'Ткаченко Лариса', phone: '5550007777' },
  davis: { teacherName: 'Данилова Вера', phone: '5550008888' },
  johnson: { teacherName: 'Иванченко Оля', phone: '5550003333' },
  wilson: { teacherName: 'Волошин Сергей', phone: '5550009999' },
  garcia: { teacherName: 'Гарбуз Мила', phone: '5550010000' },
  ivanova: { teacherName: 'Иванова Таня', phone: '5550004444' },
  petrov: { teacherName: 'Петров Максим', phone: '5550011111' },
  sokolova: { teacherName: 'Соколова Ира', phone: '5550012222' },
  lee: { teacherName: 'Лещенко Тамара', phone: '5550005555' },
  kim: { teacherName: 'Ким Виталий', phone: '5550006666' },
  clark: { teacherName: 'Кравченко Эвелина', phone: '5550013333' },
  martin: { teacherName: 'Мартынова Юля', phone: '5550014444' },
  rodriguez: { teacherName: 'Рудник Дина', phone: '5550015555' },
}

// Internal build shape: schema fields + studentRefs, which get denormalized
// into embedded students at the end of this file. The exported `classes`
// never carry refs. `id` is build-time only (EXTRA_ROSTER targeting).
const classDefs = [
  {
    id: 'SS-21',
    type: 'sundaySchool',
    class: '21',
    classroom: '16',
    day: 'СРЕДА',
    location: 'Church 1',
    teacherV2: [T.smith, T.brown, T.thompson, T.davis].map((t) => ({ ...t, role: 'teacher' })),
    studentRefs: [
      { familyId: 'F001', childId: 'C001' },
      { familyId: 'F002', childId: 'C003' },
    ],
  },
  {
    id: 'SS-34',
    type: 'sundaySchool',
    class: '34',
    classroom: '14',
    day: 'ПЯТНИЦА',
    location: 'Church 1',
    teacherV2: [T.johnson, T.wilson, T.garcia].map((t) => ({ ...t, role: 'teacher' })),
    studentRefs: [
      { familyId: 'F001', childId: 'C002' },
      { familyId: 'F003', childId: 'C004' },
    ],
  },
  {
    id: 'RS-1V',
    type: 'russianSchool',
    class: '1В',
    classroom: '4',
    day: 'Суббота',
    location: 'Church 1',
    teacherV2: [
      { ...T.ivanova, role: 'language_teacher' },
      { ...T.petrov, role: 'reading_teacher' },
      { ...T.sokolova, role: 'teacher' },
      { ...T.smith, role: 'teacher' },
    ],
    studentRefs: [{ familyId: 'F001', childId: 'C001' }],
  },
  {
    // Choir as a class document — see the OPEN QUESTION at the top of the file.
    id: 'CH-1',
    type: 'childrensChoir',
    class: '1', // choirNumber
    classroom: '20', // choirRoom
    day: 'СРЕДА',
    location: 'Church 1',
    teacherV2: [T.lee, T.kim, T.clark, T.martin, T.rodriguez, T.smith].map((t) => ({
      ...t,
      role: 'choir_leader',
    })),
    studentRefs: [
      { familyId: 'F001', childId: 'C001' },
      { familyId: 'F002', childId: 'C003' },
    ],
  },
]

// --- Roster filler for realistic volume testing -------------------------
// One family per student keeps things simple. Sunday School 21 ends up with
// 18 students; choir ~10; the rest fill out similarly. All hand-written
// records are Church 1 so class/family locations agree.
// [givenName, surname, gender, birthdate, classIds]
const EXTRA_ROSTER = [
  ['Марк', 'Резник', 'male', '2015-06-11', ['SS-21', 'CH-1']],
  ['София', 'Бондар', 'female', '2015-08-19', ['SS-21', 'RS-1V']],
  ['Давид', 'Мельник', 'male', '2016-01-07', ['SS-21']],
  ['Ева', 'Мельникова', 'female', '2015-11-23', ['SS-21', 'CH-1']],
  ['Тимофей', 'Шевченко', 'male', '2015-03-30', ['SS-21']],
  ['Анна', 'Мороз', 'female', '2016-02-14', ['SS-21', 'RS-1V', 'CH-1']],
  ['Даниил', 'Литвин', 'male', '2015-09-05', ['SS-21']],
  ['Вера', 'Кравец', 'female', '2015-12-01', ['SS-21', 'CH-1']],
  ['Самуил', 'Гордей', 'male', '2016-04-22', ['SS-21', 'RS-1V']],
  ['Милана', 'Орлова', 'female', '2015-07-16', ['SS-21', 'CH-1']],
  ['Натан', 'Белов', 'male', '2015-10-09', ['SS-21']],
  ['Алина', 'Семенова', 'female', '2016-03-03', ['SS-21', 'RS-1V']],
  ['Пётр', 'Яковлев', 'male', '2015-05-27', ['SS-21']],
  ['Диана', 'Фёдорова', 'female', '2015-08-08', ['SS-21', 'CH-1']],
  ['Илья', 'Богдан', 'male', '2016-01-29', ['SS-21', 'RS-1V']],
  ['Ника', 'Тарасова', 'female', '2015-04-17', ['SS-21', 'CH-1']],
  ['Лев', 'Данилов', 'male', '2017-06-06', ['SS-34', 'CH-1']],
  ['Майя', 'Кузьмина', 'female', '2017-09-13', ['SS-34']],
  ['Адам', 'Волков', 'male', '2018-02-20', ['SS-34']],
  ['Злата', 'Попова', 'female', '2017-12-04', ['SS-34']],
]

const PARENT_FIRSTS = [
  ['Олег', 'Ира'],
  ['Андрей', 'Наташа'],
  ['Виктор', null],
  ['Павел', 'Оля'],
  ['Роман', 'Таня'],
  ['Иван', null],
  ['Денис', 'Лена'],
]

let phoneSeq = 2000000
const nextPhone = () => `555${String(phoneSeq++)}` // 10-digit national, schema style

// Surname as it appears on the parents (masculine base form for couples).
const surnameBase = (last) => last.replace(/(ов|ев|ёв|ин)а$/, '$1')

EXTRA_ROSTER.forEach(([first, last, gender, birthdate, classIds], i) => {
  const familyId = `F${100 + i}`
  const childId = `C${100 + i}`
  const [p1, p2] = PARENT_FIRSTS[i % PARENT_FIRSTS.length]
  const parentLast = surnameBase(last)
  families.push({
    familyId,
    location: 'Church 1',
    parentName1: `${parentLast} ${p1}`,
    parentPhone1: nextPhone(),
    ...(p2 ? { parentName2: `${parentLast} ${p2}`, parentPhone2: nextPhone() } : {}),
    lastUpdated: `2025-0${1 + (i % 9)}-${String(1 + ((i * 3) % 28)).padStart(2, '0')}`,
    children: [{ childId, studentName: `${last} ${first}`, birthdate, gender }],
  })
  classIds.forEach((id) => {
    classDefs.find((c) => c.id === id).studentRefs.push({ familyId, childId })
  })
})

// --- Class catalog filler: the real church runs 60-80 classes ------------
// Generates ~46 more class documents (numbered Sunday School classes,
// Russian School grades in А/Б groups, plus choirs) so the admin UI is
// designed against catalog scale. Rosters get their own generated families;
// teachers come from a generated pool with phones (= accounts, teacherV2).
const TEACHER_POOL = [
  'Павлова Ирина', 'Ковальчук Оксана', 'Дубова Настя', 'Егорова Люба',
  'Филатова Женя', 'Гринько Даша', 'Хоменко Света', 'Исаева Роза',
  'Юрченко Галя', 'Котова Зина', 'Лаврова Мила', 'Мишина Тоня',
  'Новак Лида', 'Осипова Клара', 'Стоянова Ася', 'Руденко Поля',
  'Титова Нина', 'Черненко Аня', 'Бойко Рая', 'Кузьменко Влада',
].map((teacherName, i) => ({
  teacherName,
  phone: `555${3000000 + i}`,
}))

// Day strings mirror the examples: Sunday School / choir days are uppercase
// Russian, Russian School always meets on "Суббота" (title case, as in
// family.v2). Displayed as-is by the UI.
const SS_DAYS = ['ВОСКРЕСЕНЬЕ', 'СРЕДА', 'ПЯТНИЦА']
const CHOIR_DAYS = ['СРЕДА', 'ПЯТНИЦА']

const RS_ROLES = ['language_teacher', 'reading_teacher', 'teacher']

// Sunday School: numbered classes "1".."20" (hand-written "21"/"34" continue
// the sequence). Russian School: "1А".."9Б". Choirs: "2".."9" ("1" is hand-written).
const GEN_CLASS_DEFS = [
  ...Array.from({ length: 20 }, (_, n) => ({ type: 'sundaySchool', class: String(n + 1) })),
  ...['1', '2', '3', '4', '5', '6', '7', '8', '9'].flatMap((g) => [
    { type: 'russianSchool', class: `${g}А` },
    { type: 'russianSchool', class: `${g}Б` },
  ]),
  ...[2, 3, 4, 5, 6, 7, 8, 9].map((n) => ({ type: 'childrensChoir', class: String(n) })),
]

const KID_FIRSTS_M = ['Ной', 'Марк', 'Артём', 'Глеб', 'Исаак', 'Лука', 'Стефан', 'Егор', 'Миша', 'Йона', 'Кирилл', 'Симон', 'Филипп', 'Матвей', 'Калеб', 'Аарон', 'Богдан', 'Юра', 'Сила', 'Лёва']
const KID_FIRSTS_F = ['Кира', 'Полина', 'Руфь', 'Эсфирь', 'Дарина', 'Лиза', 'Марта', 'Надя', 'Ханна', 'Яна', 'Соня', 'Лидия', 'Вика', 'Ноеми', 'Зоя', 'Клара', 'Таисия', 'Лада', 'Мириам', 'Ульяна']
const KID_LASTS = ['Антонов', 'Баранов', 'Чернов', 'Демидов', 'Ефимов', 'Фролов', 'Гусев', 'Хоменко', 'Игнатов', 'Жаров', 'Калинин', 'Лебедев', 'Макаров', 'Никитин', 'Осипов', 'Панков', 'Рогов', 'Савин', 'Тихонов', 'Устинов', 'Власов', 'Вирт', 'Юдин', 'Зайцев', 'Аверин', 'Быков', 'Черкасов', 'Дорохов', 'Ерёмин', 'Фадеев']

// Each generated class gets its OWN generated students (one small family per
// kid) — a real kid attends 1-2 programs, not 15. Birth years track the class.
// Deterministic integer hash — breaks the arithmetic patterns that linear
// index math produces (same-surname families kept drawing the same two
// first names).
const mix = (x) => {
  let h = x ^ 0x9e3779b9
  h = Math.imul(h ^ (h >>> 16), 0x45d9f3b)
  h = Math.imul(h ^ (h >>> 13), 0x45d9f3b)
  return (h ^ (h >>> 16)) >>> 0
}

let genSeq = 0
GEN_CLASS_DEFS.forEach((def, i) => {
  const size = 8 + ((i * 5) % 11) // 8..18 students
  const teacherCount = 3 + (i % 4) // 3..6 teachers
  const birthYear =
    def.type === 'childrensChoir' ? 2014 + (i % 7) :
    def.type === 'russianSchool' ? 2019 - Number(def.class[0]) :
    2021 - Math.floor((Number(def.class) - 1) / 2)
  // First run: locations are mostly ABSENT. One class = one church; every
  // member family shares the class location (or its absence).
  const classLoc = i % 8 === 0 ? 'Church 1' : i % 8 === 4 ? 'Church 2' : undefined

  const studentRefs = Array.from({ length: size }, (_, k) => {
    const n = genSeq++
    const familyId = `F${1000 + n}`
    const childId = `C${1000 + n}`
    const gender = mix(n) % 2 ? 'male' : 'female'
    const first = (gender === 'male' ? KID_FIRSTS_M : KID_FIRSTS_F)[mix(n + 101) % 20]
    const last = KID_LASTS[mix(n + 202) % KID_LASTS.length]
    // Slavic surnames take the feminine form for girls (Антонов -> Антонова)
    const kidLast = gender === 'female' && /(ов|ев|ёв|ин)$/.test(last) ? `${last}а` : last
    const [p1, p2] = PARENT_FIRSTS[mix(n + 303) % PARENT_FIRSTS.length]
    families.push({
      familyId,
      ...(classLoc ? { location: classLoc } : {}),
      parentName1: `${last} ${p1}`,
      parentPhone1: `555${4000000 + n * 2}`,
      ...(p2 ? { parentName2: `${last} ${p2}`, parentPhone2: `555${4000001 + n * 2}` } : {}),
      lastUpdated: `2025-0${1 + (mix(n + 404) % 9)}-${String(1 + (mix(n + 505) % 28)).padStart(2, '0')}`,
      children: [{
        childId,
        studentName: `${kidLast} ${first}`,
        birthdate: `${birthYear}-${String(1 + ((i + k) % 12)).padStart(2, '0')}-${String(1 + ((i * 3 + k * 5) % 28)).padStart(2, '0')}`,
        gender,
      }],
    })
    return { familyId, childId }
  })

  classDefs.push({
    id: `GEN-${i}`,
    type: def.type,
    class: def.class,
    classroom:
      def.type === 'sundaySchool' ? `${1 + (i % 30)}` :
      def.type === 'russianSchool' ? `${1 + (i % 28)}` : `${15 + (i % 10)}`,
    day:
      def.type === 'russianSchool' ? 'Суббота' :
      def.type === 'childrensChoir' ? CHOIR_DAYS[i % CHOIR_DAYS.length] :
      SS_DAYS[i % SS_DAYS.length],
    ...(classLoc ? { location: classLoc } : {}),
    teacherV2: Array.from({ length: teacherCount }, (_, t) => ({
      ...TEACHER_POOL[(i * 3 + t) % TEACHER_POOL.length],
      role:
        def.type === 'childrensChoir' ? 'choir_leader' :
        def.type === 'russianSchool' ? RS_ROLES[t % RS_ROLES.length] : 'teacher',
    })),
    studentRefs,
  })
})

// --- Finalize class documents: denormalize students, drop build-time refs --

const familyById = new Map(families.map((f) => [f.familyId, f]))

const denormalizeStudent = ({ familyId, childId }) => {
  const family = familyById.get(familyId)
  const child = family?.children.find((c) => c.childId === childId)
  if (!family || !child) return null
  return {
    childId: child.childId,
    familyId: family.familyId,
    studentName: child.studentName,
    birthdate: child.birthdate,
    gender: child.gender,
    parentName1: family.parentName1 || '',
    parentPhone1: family.parentPhone1 || '',
    parentName2: family.parentName2 || '',
    parentPhone2: family.parentPhone2 || '',
  }
}

// Exported class documents — pure schema shape (+ optional location).
export const classes = classDefs.map(({ id, studentRefs, ...cls }) => ({
  type: cls.type,
  class: cls.class,
  classroom: cls.classroom,
  day: cls.day,
  ...(cls.location ? { location: cls.location } : {}),
  teachers: cls.teacherV2.map((t) => t.teacherName), // legacy names, kept in parallel
  teacherV2: cls.teacherV2,
  students: studentRefs.map(denormalizeStudent).filter(Boolean),
}))

// --- Attach embedded activity blocks to family children ------------------
// family.v2 keeps a copy of each activity on the child; derive those copies
// from the class documents so the two shapes can never disagree.
for (const cls of classes) {
  for (const s of cls.students) {
    const child = familyById.get(s.familyId)?.children.find((c) => c.childId === s.childId)
    if (!child) continue
    if (cls.type === 'childrensChoir') {
      child.childrensChoir = {
        choirLeaders: cls.teachers,
        choirLeaderV2: cls.teacherV2.map((t) => ({
          choirLeaderName: t.teacherName,
          phone: t.phone,
          role: t.role,
        })),
        choirNumber: cls.class,
        choirRoom: cls.classroom,
        day: cls.day,
      }
    } else {
      child[cls.type] = {
        class: cls.class,
        classroom: cls.classroom,
        day: cls.day,
        teachers: cls.teachers,
        teacherV2: cls.teacherV2,
      }
    }
  }
}

// Admins are never teachers; they may also be parents.
export const admins = [{ name: 'Матвеев Руслан', phone: '5558887777' }]

// Demo login that gets all three modules at once (mock-only convenience —
// in reality a person would be admin+parent or teacher+parent, not all 3).
export const DEMO_ALL_MODULES_PHONE = '+15550000000'

// ---------------------------------------------------------------------------
// Internal helpers
// ---------------------------------------------------------------------------

const normalize = (phone) => String(phone || '').replace(/\D/g, '').replace(/^1(?=\d{10}$)/, '')

const samePhone = (a, b) => normalize(a) === normalize(b)

// Class documents carry no id; the schema's uniqueness key is
// type + class + classroom + day. The FE derives a stable classId from it
// for React keys and drill-down routing (compat shim, not sent by the BE).
export const classKeyOf = (cls) => `${cls.type}:${cls.class}:${cls.classroom}:${cls.day}`

export const projectClass = (cls) => ({ classId: classKeyOf(cls), ...cls })

const classesOfChild = (childId) =>
  classes.filter((c) => c.students.some((s) => s.childId === childId))

// Per-class summary attached to admin students/families (no roster copy).
const classSummary = (c) => {
  const { students, ...rest } = c
  return projectClass(rest)
}

// ---------------------------------------------------------------------------
// Module projections — what the BE would return per login
// ---------------------------------------------------------------------------

/** Family module: today's parent payload (family.v2 blob). */
export const buildFamilyModule = (phone) =>
  families.find((f) => samePhone(f.parentPhone1, phone) || samePhone(f.parentPhone2, phone)) || null

/** Teacher module: rosters for the classes where this phone teaches.
 *  teacherV2 phones are the account ids. */
export const buildTeacherModule = (phone) => {
  const mine = classes.filter((c) => c.teacherV2.some((t) => samePhone(t.phone, phone)))
  return mine.length ? { classes: mine.map(projectClass) } : null
}

/** Admin module: every class with its roster, a flat student list, and a
 *  family list — everything the admin smart search filters client-side.
 *  Students/parents use the schema's denormalized parentName1/2 fields. */
export const buildAdminModule = () => ({
  classes: classes.map(projectClass),
  families: families.map((family) => ({
    familyId: family.familyId,
    // parentName1 is "Фамилия Имя" — the surname is the first token
    familyName: `${(family.parentName1 || '').trim().split(' ')[0]} Family`,
    ...(family.location ? { location: family.location } : {}),
    parentName1: family.parentName1 || '',
    parentPhone1: family.parentPhone1 || '',
    parentName2: family.parentName2 || '',
    parentPhone2: family.parentPhone2 || '',
    children: family.children.map((child) => ({
      childId: child.childId,
      studentName: child.studentName,
      birthdate: child.birthdate,
      gender: child.gender,
      classes: classesOfChild(child.childId).map(classSummary),
    })),
  })),
  students: families.flatMap((family) =>
    family.children.map((child) => ({
      childId: child.childId,
      familyId: family.familyId,
      studentName: child.studentName,
      birthdate: child.birthdate,
      gender: child.gender,
      ...(family.location ? { location: family.location } : {}),
      parentName1: family.parentName1 || '',
      parentPhone1: family.parentPhone1 || '',
      parentName2: family.parentName2 || '',
      parentPhone2: family.parentPhone2 || '',
      // full per-class detail so the admin student view can show everything
      // the family page shows for a child
      classes: classesOfChild(child.childId).map(classSummary),
    }))
  ),
})

/**
 * Full mock login payload: which modules this phone gets, and their data.
 * Mirrors what /family (or its successor) would return per person.
 */
export const resolveModules = (phone) => {
  const all = samePhone(phone, DEMO_ALL_MODULES_PHONE)
  const family = all ? families[0] : buildFamilyModule(phone)
  const teacher = all ? buildTeacherModule(classes[0].teacherV2[0].phone) : buildTeacherModule(phone)
  const admin = all || admins.some((a) => samePhone(a.phone, phone)) ? buildAdminModule() : null

  const modules = {}
  if (family) modules.family = family
  if (teacher) modules.teacher = teacher
  if (admin) modules.admin = admin

  const defaultModule = family ? 'family' : teacher ? 'teacher' : admin ? 'admin' : null
  return defaultModule ? { defaultModule, modules } : null
}
